import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';

interface TagInputProps {
    tags: string[];
    onTagAdd: (tag: string) => void;
    onTagRemove: (tag: string) => void;
    disabled?: boolean;
    availableTags?: string[];
}

const TagInput: React.FC<TagInputProps> = ({ tags, onTagAdd, onTagRemove, disabled = false, availableTags = [] }) => {
    const [inputValue, setInputValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Filter suggestions based on input
    useEffect(() => {
        if (inputValue.trim() && availableTags.length > 0) {
            const filtered = availableTags
                .filter(tag =>
                    tag.toLowerCase().includes(inputValue.toLowerCase()) &&
                    !tags.includes(tag) // Don't suggest tags already added
                )
                .slice(0, 5); // Limit to 5 suggestions
            setSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
        setSelectedSuggestionIndex(-1);
    }, [inputValue, availableTags, tags]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (disabled) return;

        // Navigate suggestions with arrow keys
        if (showSuggestions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedSuggestionIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
            } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
                e.preventDefault();
                handleSuggestionSelect(suggestions[selectedSuggestionIndex]);
            }
        }

        // Add tag on Enter or comma
        if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
            e.preventDefault();

            // If a suggestion is selected, use it
            if (selectedSuggestionIndex >= 0) {
                handleSuggestionSelect(suggestions[selectedSuggestionIndex]);
            } else {
                // Otherwise add the exact input
                addTag(inputValue.trim());
            }
        } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            onTagRemove(tags[tags.length - 1]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setSelectedSuggestionIndex(-1);
        }
    };

    const addTag = (tag: string): void => {
        // Normalize tag (trim, lowercase for comparison)
        const normalizedTag = tag.trim();

        // Check for duplicates (case-insensitive)
        const isDuplicate = tags.some(existingTag =>
            existingTag.toLowerCase() === normalizedTag.toLowerCase()
        );

        if (!isDuplicate && normalizedTag) {
            onTagAdd(normalizedTag);
            setInputValue('');
            setShowSuggestions(false);
        }
    };

    const handleSuggestionSelect = (suggestion: string): void => {
        addTag(suggestion);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        // Remove commas from input (since we use comma as a shortcut)
        const cleanValue = value.replace(/,/g, '');
        setInputValue(cleanValue);
    };

    const handleSuggestionClick = (suggestion: string): void => {
        handleSuggestionSelect(suggestion);
    };

    return (
        <div className="tag-input">
            <div className="tags-container">
                {tags.map(tag => (
                    <span key={tag} className="tag">
                        {tag}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => onTagRemove(tag)}
                                className="tag-remove"
                            >
                                ×
                            </button>
                        )}
                    </span>
                ))}
                <div className="tag-input-wrapper">
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setShowSuggestions(suggestions.length > 0)}
                        placeholder={disabled ? "Saving..." : "Type to add tags..."}
                        className="tag-input-field"
                        disabled={disabled}
                    />

                    {/* Suggestions dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                        <div ref={suggestionsRef} className="tag-suggestions">
                            {suggestions.map((suggestion, index) => (
                                <div
                                    key={suggestion}
                                    className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''
                                        }`}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                                >
                                    {suggestion}
                                    <span className="suggestion-hint">Click or press Enter</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Input hints */}
            {!disabled && (
                <div className="tag-input-hints">
                    <span>Press Enter or comma to add tags</span>
                    {availableTags.length > 0 && (
                        <span>• Use arrow keys to navigate suggestions</span>
                    )}
                </div>
            )}
        </div>
    );
};


export default TagInput;